import {type FunctionalComponent, h} from '@stencil/core';
import type {Strategies} from '../../../../../strategies/Strategies';
import type {CtaWidgetv2} from '../../../../../widgets/cta-widget-v2/cta-widget-v2';

type WidgetsRowProps = {
  labelProvider: CallableFunction;
  country?: string;
  language?: string;
  strategies: Strategies,
};

type CtaWidgetDataProps = Partial<CtaWidgetv2> & { iconName: string; library?: string; };

export const CtaWidgetsRow: FunctionalComponent<WidgetsRowProps> = (props
) => {

    const {
      country,
      language,
      strategies,
      labelProvider,
    } = props;

    const isDesignFirm = strategies?.strategyData?.cardType?.params?.designFirmWidgets;
    const isDistributorStockAvailable = strategies?.strategyData?.cardType?.params?.showDsaLink;

    const widgetData: CtaWidgetDataProps[] = [
      {
        header: labelProvider('dashboardCta.productsv2.header', 'Product Selector'),
        content: labelProvider('dashboardCta.productsv2.content', 'Effortlessly discover the products and accessories tailored for your applications.'),
        url: `/${country}/${language}/work/support/product-selector/`,
        iconName: 'squares-checked',
        buttonText: labelProvider('dashboardCta.productsv2.button', 'Use Product Selector'),
        eventCategory: 'Product selector'
      },
      {
        header: labelProvider('dashboardCta.substitutionv2.header', 'Product Substitution Tool'),
        content: labelProvider('dashboardCta.substitutionv2.content', 'Get replacements and find substitutions for discontinued Schneider Electric products.'),
        url: '/myschneider/productSubsTool?search=',
        iconName: 'arrows-loop',
        buttonText: labelProvider('dashboardCta.substitutionv2.button', 'Find replacements'),
        eventCategory: 'Product substitution tool'
      },
      {
        header: labelProvider('dashboardCta.helpCenter.header',
          'Help Center'),
        content: labelProvider( 'dashboardCta.helpCenter.content',
          'Find support, popular tools and resources for all your needs in one place.'),
        url: '/myschneider/contact',
        iconName: 'question-mark',
        buttonText: labelProvider('dashboardCta.button.exploreNow',
          'Explore now'),
        eventCategory: 'Help Center'
      },
      {
        header: labelProvider('dashboardCta.ourSolutions.header',
          'Discover our solutions'),
        content: labelProvider( 'dashboardCta.ourSolutions.content',
          'Maximize energy usage and access energy management solutions anytime.'),
        url: `https://www.se.com/${country}/${language}/work/solutions/`,
        iconName: 'light-bulb-on',
        buttonText: labelProvider('dashboardCta.button.exploreNow',
          'Explore now'),
        eventCategory: 'Discover our solutions'
      }
    ];

    const designFirmwidgetData: CtaWidgetDataProps[] = [
      {
        header: labelProvider('dashboardCta.tool.header', 'EcoStruxure™ Specification Tool'),
        content: labelProvider('dashboardCta.tool.content', 'Generate detailed technical specifications for electrical and mechanical requirements!'),
        url: 'https://ecostruxure-specification.se.app/au/',
        iconName: 'breaker-signal-waves-left',
        buttonText: labelProvider('dashboardCta.button.exploreNow',
          'Explore now'),
        eventCategory: 'EcoStructure Specification Tool'
      },

      {
        header: labelProvider('dashboardCta.training.header', ' Looking for a training?'),
        content: labelProvider('dashboardCta.training.content', 'Access all available Training Courses tailored to your needs.'),
        url: ' https://schneider-electric.csod.com/ui/lms-learner-home/home',
        iconName: 'graduation-hat',
        buttonText: labelProvider('dashboardCta.button.exploreNow',
          'Explore now'),
        eventCategory: 'Trainings'
      },

      {
        header: labelProvider('dashboardCta.solutions.header', 'EcoStruxure™ Solutions for your Business'),
        content: labelProvider('dashboardCta.solutions.content', 'Access all the topics you need in one convenient place.'),
        url: `https://www.se.com/myschneider/content/${country}/en/cc/${country}/general/ecostruxure-for-segments.jsp`,
        iconName: 'cloud-book',
        buttonText: labelProvider('dashboardCta.button.exploreNow',
          'Explore now'),
        eventCategory: 'EcoStructure Solutions'
      }
    ];

    let dataSource = isDesignFirm ? designFirmwidgetData : widgetData;

    if (isDistributorStockAvailable) {
      dataSource = [{
        eventCategory: 'widget-cta-support-button',
        header: labelProvider('distributorStockAvailability.header', 'Distributor Stock Availability'),
        content: labelProvider('distributorStockAvailability.ctaWidget.content', 'Check stock status of our distribution partners with required quantities.'),
        url: '/myschneider/distributor-stock-availability',
        iconName: 'signal-waves-cube',
      } as CtaWidgetDataProps].concat(dataSource.slice(0,2));
    }

    return dataSource.map(({iconName, ...props}) =>
      <uiaas-search-cta-widget-v2
        {...props}
        anchor-target="_blank"
        tab-name= "se/all"
        extended-button={isDesignFirm}
      >
        <qds-icon
          slot="icon"
          size="large" name={iconName}
          className="dashboard-widget-icon"
        />
      </uiaas-search-cta-widget-v2>);
};
